import { useEffect } from 'react';
import useAppStore from '../store/useAppStore';

const useKeyboardShortcuts = () => {
  const {
    selectionMode,
    selectedCells,
    copiedCells,
    holidayMode,
    copySelectedCells,
    cutSelectedCells,
    pasteToSelectedCells,
    deleteSelectedCells,
    clearCutMode,
    toggleHolidayMode,
    setNotification
  } = useAppStore();

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle shortcuts when selection mode is active
      if (!selectionMode) return;

      // Don't handle shortcuts when typing in input fields
      if (
        event.target.tagName === 'INPUT' || 
        event.target.tagName === 'TEXTAREA' ||
        event.target.contentEditable === 'true'
      ) {
        return;
      }

      const hasSelection = selectedCells.size > 0;
      const hasCopiedData = copiedCells.length > 0;

      // Ctrl+C - Copy
      if (event.ctrlKey && event.key === 'c') {
        event.preventDefault();
        
        if (!hasSelection) {
          setNotification({
            type: 'warning',
            message: 'Najpierw zaznacz komórki do skopiowania (Ctrl+C)'
          });
          return;
        }
        
        copySelectedCells();
        setNotification({
          type: 'success',
          message: `Skopiowano ${selectedCells.size} komórek (Ctrl+C)`
        });
      }

      // Ctrl+X - Cut
      else if (event.ctrlKey && event.key === 'x') {
        event.preventDefault();
        
        if (!hasSelection) {
          setNotification({
            type: 'warning',
            message: 'Najpierw zaznacz komórki do wycięcia (Ctrl+X)'
          });
          return;
        }
        
        cutSelectedCells();
        setNotification({
          type: 'success',
          message: `Wycięto ${selectedCells.size} komórek (Ctrl+X)`
        });
      }

      // Ctrl+V - Paste
      else if (event.ctrlKey && event.key === 'v') {
        event.preventDefault();
        
        if (!hasSelection) {
          setNotification({
            type: 'warning',
            message: 'Najpierw zaznacz komórki, do których chcesz wkleić (Ctrl+V)'
          });
          return;
        }
        
        if (!hasCopiedData) {
          setNotification({
            type: 'warning',
            message: 'Brak danych do wklejenia. Najpierw skopiuj lub wytnij komórki (Ctrl+C/Ctrl+X)'
          });
          return;
        }
        
        pasteToSelectedCells().catch(error => {
          console.error('Error pasting cells:', error);
        });
      }

      // Delete - Delete selected cells
      else if (event.key === 'Delete') {
        event.preventDefault();
        
        if (!hasSelection) {
          setNotification({
            type: 'warning',
            message: 'Najpierw zaznacz komórki do usunięcia (Delete)'
          });
          return;
        }
        
        deleteSelectedCells().catch(error => {
          console.error('Error deleting cells:', error);
        });
      }

      // Ctrl+H - Toggle Holiday Mode
      else if (event.ctrlKey && event.key === 'h') {
        event.preventDefault();
        
        toggleHolidayMode();
        const newHolidayMode = !holidayMode;
        setNotification({
          type: 'info',
          message: newHolidayMode ? 
            'Tryb świąt WŁĄCZONY - kliknij nagłówki dni aby dodać święta (Ctrl+H)' : 
            'Tryb świąt WYŁĄCZONY (Ctrl+H)'
        });
      }

      // Ctrl+A - Select all (future enhancement)
      else if (event.ctrlKey && event.key === 'a') {
        event.preventDefault();
        // TODO: Implement select all functionality
        setNotification({
          type: 'info',
          message: 'Zaznacz wszystko (Ctrl+A) - funkcja w przygotowaniu'
        });
      }

      // Escape - Clear selection and cut mode
      else if (event.key === 'Escape') {
        event.preventDefault();
        useAppStore.getState().clearSelection();
        clearCutMode();
        setNotification({
          type: 'info',
          message: 'Wyczyszczono zaznaczenie (Escape)'
        });
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectionMode,
    selectedCells,
    copiedCells,
    holidayMode,
    copySelectedCells,
    cutSelectedCells,
    pasteToSelectedCells,
    deleteSelectedCells,
    clearCutMode,
    toggleHolidayMode,
    setNotification
  ]);

  // Return information about available shortcuts for display
  return {
    shortcuts: [
      { key: 'Ctrl+C', description: 'Kopiuj zaznaczone komórki' },
      { key: 'Ctrl+X', description: 'Wytnij zaznaczone komórki' },
      { key: 'Ctrl+V', description: 'Wklej do zaznaczonych komórek' },
      { key: 'Delete', description: 'Usuń zawartość zaznaczonych komórek' },
      { key: 'Ctrl+H', description: 'Włącz/wyłącz tryb dodawania świąt' },
      { key: 'Escape', description: 'Wyczyść zaznaczenie' }
    ]
  };
};

export default useKeyboardShortcuts;